// Shared selection set for `entityList` / `createEntity` / `updateEntity` responses.
// Used by both the client GraphQL service and the server-side cache prefetcher.
// Add new entity_fields columns here so both paths stay in sync.
//
// NOTE: `is_readable`/`is_writeable` are registry-derived flags on EntityField,
// not real `entity_fields` columns. They are resolvable ONLY on the read path
// (`entityList`, served by EntityController which reads them as hydrated model
// attributes). The create/update mutation return is served by the generic
// ModelService SQL builder, which queries the registered `entity_fields` and
// throws ("Model field EntityField.is_readable is not available!") for these
// virtual fields. The same holds for Entity's `can_*` capability flags, which are
// computed attributes rather than columns. So they are included only in the read
// selection — see `buildEntityDataSelection`.
const buildEntityDataSelection = (includeRegistryFlags: boolean) => `data {
        id
        name
        is_system_entity
        is_aro${
          includeRegistryFlags
            ? `
        can_list
        can_get_by_id`
            : ""
        }
        entity_fields(pagination: { page: 1, per_page: 10000 }) {
          data {
            id
            name
            validation_string
            data_type${
              includeRegistryFlags
                ? `
            is_readable
            is_writeable`
                : ""
            }
            default_value
            form_type
            format_decimals
            format_grouping
            format_currency
            format_locale
            created_at
            updated_at
            variant_default_entity_instance_id
            variant_entity_field {
              data {
                id
              }
            }
            entity_field_enum_values {
              data {
                id
                value
              }
            }
            entity_sequence {
              data {
                id
                name
                locked
              }
            }
          }
        }
        entity_relations(pagination: { page: 1, per_page: 10000 }) {
          data {
            id
            type
            field_name
            target_field_name
            entity_is_tightly_coupled
            target_entity_is_tightly_coupled
            entity {
              data {
                id
                name
              }
            }
            target_entity {
              data {
                id
                name
              }
            }
            media_download_is_public
            media_allowed_computed_mime_types
            media_allowed_computed_extentions
            media_allowed_mime_types
            created_at
            updated_at
          }
        }
        entity_related(pagination: { page: 1, per_page: 10000 }) {
          data {
            id
            type
            field_name
            target_field_name
            entity_is_tightly_coupled
            target_entity_is_tightly_coupled
            entity {
              data {
                id
                name
              }
            }
            target_entity {
              data {
                id
                name
              }
            }
            created_at
            updated_at
          }
        }
      }
`;

// Read selection (entityList). Includes the registry flags consumed by
// entity-form-schema (`is_readable`).
export const ENTITY_DATA_SELECTION = buildEntityDataSelection(true);

// Mutation-return selection (createEntity / updateEntity). Omits the registry
// flags the generic ModelService return path cannot resolve.
export const ENTITY_DATA_SELECTION_MUTATION = buildEntityDataSelection(false);
